<template>
  <div class="add-staff-category">
    <v-form
      @submit.prevent="submitCategoryDetails"
      v-model="submitForm"
      ref="form"
    >
      <v-row class="mt-1">
        <v-col cols="8">
          <h1 class="text-left ml-8 my-2">
            <span v-if="!is_editing">Add</span>
            <span v-else>Edit</span> Staff Category
          </h1>
          <p class="text-left ml-8">Classify Staff based on their category.</p>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="3" align-self="center" v-if="currentUser.privileges.canManageStaffAttendanceCategories">
          <v-row justify="end" class="mr-4">
            <v-btn
              v-if="is_editing"
              color="red"
              class="my-2"
              @click="showDeleteDialog"
              rounded
              outlined
              >Delete</v-btn
            >
          </v-row>
        </v-col>
      </v-row>

      <v-row class="mx-1">
        <v-col class="cols-12 pt-0">
          <v-card>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <p class="text-left mx-2 mb-1">Category Name</p>
                  <v-text-field
                    v-model="category.title"
                    outlined
                    :rules="[rules.required, rules.category_name]"
                    dense
                    placeholder="eg. Primary Teachers"
                    class="rounded-input"
                    hide-details="auto"
                    maxlength="55"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <p class="text-left mx-2 mb-1">Code</p>
                  <v-text-field
                    v-model="category.code"
                    outlined
                    counter="4"
                    :rules="[rules.required, rules.code]"
                    dense
                    placeholder="eg. PT"
                    class="rounded-input"
                    maxlength="4"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="mt-6">
                <v-col cols="6" md="3">
                  <p class="text-left mx-2 mb-1">Full Day Hours</p>
                  <v-autocomplete
                    dense
                    rounded
                    :rules="[rules.required]"
                    placeholder="hours (eg. 08:00:00)"
                    :items="getFullTime()"
                    outlined
                    class="rounded-input"
                    v-model="fullDayHours"
                    @change="clearFields()"
                  >
                    <!-- clear half day and buffer at change -->
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3">
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="minutes (eg. 08:00:00)"
                    :rules="[rules.required]"
                    class="my-7 rounded-input"
                    :items="getMinutes()"
                    outlined
                    v-model="fullDayMinutes"
                    @change="clearFields()"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3">
                  <p class="text-left mx-2 mb-1">Half Day Hours</p>
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="hours (eg. 04:00:00)"
                    :items="getHalfTime()"
                    :rules="[rules.required]"
                    class="my-1 rounded-input"
                    outlined
                    :disabled="!fullDayHours || !fullDayMinutes"
                    v-model="halfDayHours"
                    @change="clearBufferFields()"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3">
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="minutes (eg. 04:00:00)"
                    class="my-7 rounded-input"
                    :rules="[rules.required]"
                    :items="getHalfMinutes()"
                    outlined
                    v-model="halfDayMinutes"
                    :disabled="!fullDayHours || !fullDayMinutes"
                    @change="clearBufferFields()"
                  >
                  </v-autocomplete>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              <v-row>
                <v-col cols="6" md="3" v-if="overTimeSwitch">
                  <p class="text-left mx-2 mb-1">Minimum Overtime</p>
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="hours (eg. 08:00:00)"
                    :items="getFullTime()"
                    outlined
                    class="rounded-input"
                    v-model="overTimeHours"
                    :rules="[rules.required]"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3" v-if="overTimeSwitch">
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="minutes (eg. 08:00:00)"
                    class="my-7 rounded-input"
                    :items="getMinutes()"
                    :rules="[rules.required]"
                    outlined
                    v-model="overTimeMinutes"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3">
                  <v-switch
                    v-model="overTimeSwitch"
                    label="Allow overtime"
                    class="my-7 mx-5"
                  >
                  </v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" sm="6" md="4" v-if="!flexibleHoursSwitch">
                  <!-- Shift Start time-picker -->
                  <p class="text-left mx-2 mb-1">Shift Start Time</p>

                  <v-dialog
                    ref="shiftStartTimeDialog"
                    v-model="shiftStartTimeDialog"
                    :return-value.sync="shiftStartTime"
                    width="300px"
                    required
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="shiftStartTime ? moment(shiftStartTime, 'HH:mm').format('LT'): null"
                        outlined
                        dense
                        class="rounded-input"
                        prepend-icon="mdi-clock-start"
                        readonly
                        v-bind="attrs"
                        :rules="[rules.required]"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="shiftStartTimeDialog"
                      v-model="shiftStartTime"
                      @change="shiftEndTime=null"
                      format="ampm"
                      width="300px"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="shiftStartTimeDialog = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.shiftStartTimeDialog.save(shiftStartTime)"
                      >
                        OK
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>

                <v-col cols="12" sm="6" md="4" v-if="!flexibleHoursSwitch">
                  <!-- Shift Start time-picker -->
                  <p class="text-left mx-2 mb-1">Shift End Time</p>

                  <v-dialog
                    ref="shiftEndTimeDialog"
                    v-model="shiftEndTimeDialog"
                    :return-value.sync="shiftEndTime"
                    width="300px"
                    required
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="shiftEndTime ? moment(shiftEndTime, 'HH:mm').format('LT') : null"
                        outlined
                        dense
                        class="rounded-input"
                        prepend-icon="mdi-clock-start"
                        v-bind="attrs"
                        readonly
                        :rules="[rules.required]"
                        :disabled="!shiftStartTime"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="shiftEndTimeDialog"
                      v-model="shiftEndTime"
                      :allowed-hours="generateArray()"
                      format="ampm"
                      width="300px"
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="shiftEndTimeDialog = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.shiftEndTimeDialog.save(shiftEndTime)"
                      >
                        OK
                      </v-btn>
                    </v-time-picker>
                  </v-dialog>
                </v-col>
                
                <v-col cols="6" md="4">
                  <v-switch
                    v-model="flexibleHoursSwitch"
                    class="my-7 mx-5"
                  >
                    <template v-slot:label>
                      <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        color="primary"
                        class="mx-2"
                        dark
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-help-circle
                      </v-icon>
                    </template>
                    <span>Staff that belong to this group are exempt from late-marks and the calculation of attendance related particulars on the payslip will be based on Full-day hours and Half-day hours only.</span>
                  </v-tooltip>
                      Flexible Hours 

                    </template> 
                  </v-switch>
                </v-col> 
              </v-row>
              <v-row v-if="!flexibleHoursSwitch">
                <v-col cols="6" md="4">
                  <p class="text-left mx-2 mb-1">
                    Allowed Time Before Late Mark
                  </p>
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="hours (eg. 04:00)"
                    :items="getBufferTime()"
                    class="my-1 rounded-input"
                    outlined
                    :rules="[rules.required]"
                    v-model="allowed_late_hours"
                    :disabled="!halfDayHours || !halfDayMinutes"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="4">
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="minutes (eg. 00:15)"
                    class="my-7 rounded-input"
                    :rules="[rules.required]"
                    :items="getBufferMinutes()"
                    outlined
                    v-model="allowed_late_minutes"
                    :disabled="!halfDayHours || !halfDayMinutes"
                  >
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6" md="3">
                  <p class="text-left mx-2 mb-1">
                    Buffer Time
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            color="primary"
                            class="mx-2"
                            dark
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-help-circle
                          </v-icon>
                        </template>
                    <span>The time span allowed to check-in and checkout, after and before the full-day or half-day hours.</span>
                  </v-tooltip>

                  </p>
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="hours (eg. 04:00:00)"
                    :items="getBufferTime()"
                    class="my-1 rounded-input"
                    :rules="[rules.required]"
                    outlined
                    v-model="bufferHours"
                    :disabled="!halfDayHours || !halfDayMinutes"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col cols="6" md="3">
                  <v-autocomplete
                    dense
                    rounded
                    placeholder="minutes (eg. 04:00:00)"
                    class="my-7 rounded-input"
                    :rules="[rules.required]"
                    :items="getBufferMinutes()"
                    outlined
                    v-model="bufferMinutes"
                    :disabled="!halfDayHours || !halfDayMinutes"
                  >
                  </v-autocomplete>
                </v-col>
                <v-col>
                  <p class="text-left mx-2 mb-1">Weekoffs</p>
                  <v-select
                    dense
                    rounded
                    outlined
                    placeholder="Select weekoffs"
                    class="my-1 rounded-input"
                    :items="weekdays"
                    item-text="display_name"
                    item-value="day_index"
                    v-model="category.weekoffs"
                    multiple
                    chips
                  >
                  </v-select>
                </v-col>
                <v-spacer></v-spacer>
              </v-row>
              
              <StaffAttendanceCategoryTimeBar 
                :shiftStartTime="shiftStartTime"
                :shiftEndTime="shiftEndTime"
                v-if="shiftStartTime && shiftEndTime"
                :bufferTime="bufferHours && bufferMinutes ? `${bufferHours.split(' ')[0]}:${bufferMinutes.split(' ')[0]}` : null"
                :allowedTimeBeforeLateMark="allowed_late_hours && allowed_late_minutes ? `${allowed_late_hours.split(' ')[0]}:${allowed_late_minutes.split(' ')[0]}`: null"
                :minimumOvertime="overTimeSwitch && overTimeHours && overTimeMinutes ?  `${overTimeHours.split(' ')[0]}:${overTimeMinutes.split(' ')[0]}` : null"
              />

              <div class="text-right mx-4 my-4" v-if="currentUser.privileges.canManageStaffAttendanceCategories">
                <v-btn
                  class="mr-4"
                  color="red"
                  :to="{ name: 'AttendanceSettingsCategory' }"
                  rounded
                  outlined
                >
                  Cancel
                </v-btn>
                <!-- Removed disabled from submit button :disabled="isLoading || !submitForm" -->
                <v-btn color="primary" type="submit" v-model="submitForm" :disabled="!submitForm" rounded>Submit</v-btn>
              </div>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <DeleteConfirmationDialog
      title="Are you sure you want to delete this Staff Category?"
      :visible="showDeleteConfirmation"
      @deleteObject="deleteObjectConfirmation"
    />
    <v-row justify="center">
      <ConfirmationDialog
        title="Do you want to change all the past records of all the staff of this category if any?"
        successText="Yes"
        @successCallback="changePastRecords()"
        failureText="No"
        @failureCallback="closeDialog"
        :visible="dialog"
      >
      </ConfirmationDialog>
    </v-row>

    <v-snackbar
      v-model="snackbar.visible"
      timeout="5000"
      :color="snackbar.color"
      multi-line
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar.visible = false"
          >Close</v-btn
        >
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import endpoints from "@api/repository";
import api from "@api/index";
import DeleteConfirmationDialog from "@components/widgets/dialogs/DeleteConfirmationDialog";
import ConfirmationDialog from "@components/widgets/dialogs/ConfirmationDialog";
import moment from 'moment';
import StaffAttendanceCategoryTimeBar from '@components/staff_attendance/StaffAttendanceCategoryTimeBar';


export default {
  name: "AddEditStaffCategory",
  components: {
    DeleteConfirmationDialog,
    ConfirmationDialog,
    StaffAttendanceCategoryTimeBar,
  },
  data() {
    return {
      dialog: false,
      is_editing: false,
      submitForm: false,
      shiftStartTimeDialog: false,
      shiftEndTimeDialog: false,
      showDeleteConfirmation: false,
      moment: moment,
      rules: {
        required: (v) => !!v || "This field is required",
        category_name: (v) => /^[a-zA-Z0-9_ ]{1,55}$/.test(v) || "Invalid",
        code: (v) => /^[a-zA-Z0-9]{1,4}$/.test(v) || "Invalid",
      },
      category: {
        title: null,
        code: null,
        weekoffs: null,
        used: null
      },
      formErrors: {
        codeError: null,
      },
      fullDayHours: null,
      fullDayMinutes: null,
      halfDayHours: null,
      halfDayMinutes: null,
      overTimeHours: null,
      overTimeMinutes: null,
      overTimeSwitch: false,
      flexibleHoursSwitch: false,
      bufferHours: null,
      bufferMinutes: null,
      allowed_late_hours: null,
      allowed_late_minutes: null,
      shiftStartTime: null,
      shiftEndTime: null,
      weekdays: [
        {
          day_index: 0,
          display_name: "Monday",
        },
        {
          day_index: 1,
          display_name: "Tuesday",
        },
        {
          day_index: 2,
          display_name: "Wednesday",
        },
        {
          day_index: 3,
          display_name: "Thursday",
        },
        {
          day_index: 4,
          display_name: "Friday",
        },
        {
          day_index: 5,
          display_name: "Saturday",
        },
        {
          day_index: 6,
          display_name: "Sunday",
        },
      ],
      snackbar: {
        visible: false,
        message: "",
        color: "dark",
      },
    };
  },
  computed: {
    ...mapGetters(["currentUser", "accessToken", "isLoading"]),
    essentials() {
      return {
        accessToken: this.accessToken,
        setLoading: this.setLoading,
        handleErrorsFunction: this.handleApiErrors,
      };
    },
  },

  methods: {
    ...mapActions(["setLoading"]),

    generateArray() {
      var end = 23;
      var start = 0;
      if(this.shiftStartTime)
      start = Number(this.shiftStartTime.split(':')[0]);
      return Array(end - start + 1).fill().map((_, idx) => start + idx);
    },

    getFullTime() {
      var fullTime = [];
      for (var i = 0; i < 24; i++) {
        var temp = String(i) + " hour";
        if (i != 1) temp += "s";

        fullTime.push(temp);
      }
      return fullTime;
    },

    getMinutes() {
      var allMinutes = [];
      for (var i = 0; i < 12; i++) {
        var temp = String(i * 5) + " minutes";
        allMinutes.push(temp);
      }
      return allMinutes;
    },

    getHalfTime() {
      var fullDay = this.fullDayHours;
      if (fullDay) fullDay = Number(fullDay.split(" ")[0]);
      else fullDay = 23; //will never execute. Just to avoid error
      var halfDayTime = [];
      if (fullDay != 0) fullDay -= 1;
      for (var i = 0; i <= fullDay; i++) {
        var temp = String(i) + " hour";
        if (i != 1) temp += "s";
        halfDayTime.push(temp);
      }
      return halfDayTime;
    },

    getHalfMinutes() {
      var num_mins = 12;
      // console.log(this.fullDayHours.split(" ")[0])
      if (this.fullDayHours && this.fullDayHours.split(" ")[0] == "0") {
        var fullDayMins = 55; // Just to avoid console error
        if (this.fullDayMinutes)
          fullDayMins = Number(this.fullDayMinutes.split(" ")[0]);
        num_mins = fullDayMins / 5;
      }
      var allMinutes = [];
      for (var i = 0; i < num_mins; i++) {
        var temp = String(i * 5) + " minutes";
        allMinutes.push(temp);
      }

      if (allMinutes.length == 0) allMinutes.push("0 minutes");
      return allMinutes;
    },

    clearFields() {
      this.halfDayHours = null;
      this.halfDayMinutes = null;
      this.bufferHours = null;
      this.bufferMinutes = null;
      this.allowed_late_hours = null;
      this.allowed_late_minutes = null;
      this.shiftStartTime = null;
      this.shiftEndTime = null;
    },

    clearBufferFields() {
      this.bufferHours = null;
      this.bufferMinutes = null;
    },

    getBufferTime() {
      var halfDay = this.halfDayHours;
      if (halfDay) halfDay = Number(halfDay.split(" ")[0]);
      else halfDay = 23; // Just to avoid error
      var newBufferTime = [];
      if (halfDay != 0) halfDay -= 1;
      for (var i = 0; i <= halfDay; i++) {
        var temp = String(i) + " hour";
        if (i != 1) temp += "s";
        newBufferTime.push(temp);
      }
      return newBufferTime;
    },

    getBufferMinutes() {
      var num_mins = 12;
      // console.log(this.fullDayHours.split(" ")[0])
      if (this.halfDayHours && this.halfDayHours.split(" ")[0] == "0") {
        var halfDayMins = 55; //Just to avoid console error
        if (this.halfDayMinutes)
          halfDayMins = Number(this.halfDayMinutes.split(" ")[0]);
        num_mins = halfDayMins / 5;
      }
      var allMinutes = [];
      for (var i = 0; i < num_mins; i++) {
        var temp = String(i * 5) + " minutes";
        allMinutes.push(temp);
      }

      if (allMinutes.length == 0) allMinutes.push("0 minutes");
      return allMinutes;
    },

    formatHours(time) {
      time = time.split(":")[0];
      if (time[0] == 0) time = time[1];
      time = time + " hour";
      if (!(time[0] == 1 && time[1]==' ')) time += "s";
      return time;
    },

    formatMinutes(time) {
      time = time.split(":")[1];
      if (time[0] == 0) time = time[1];
      time = time + " minutes";
      return time;
    },

    async getCategoryDetails() {
      var response;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: endpoints.addEditStaffCategory + "?id=" + this.$route.params.id,
        responseVar: response,
      });
      if (response) {
        // console.log(response)
        this.overTimeSwitch = response.allow_overtime;
        this.category.code = response.code;
        this.category.title = response.title;
        this.category.id = response.id;
        this.category.used = response.used;
        this.category.institution = response.institution;
        this.fullDayHours = this.formatHours(response.full_day_hours);
        this.fullDayMinutes = this.formatMinutes(response.full_day_hours);
        this.halfDayHours = this.formatHours(response.half_day_hours);
        this.halfDayMinutes = this.formatMinutes(response.half_day_hours);
        this.bufferHours = this.formatHours(response.buffer_time);
        this.bufferMinutes = this.formatMinutes(response.buffer_time);
        this.allowed_late_hours = this.formatHours(response.allowed_late_time);
        this.allowed_late_minutes = this.formatMinutes(response.allowed_late_time);
        this.flexibleHoursSwitch = response.flexible_hours;
        if(!this.flexibleHoursSwitch){
          this.shiftStartTime = response.shift_start_time;
          this.shiftEndTime = response.shift_end_time;
        }
        
        if (this.overTimeSwitch) {
          this.overTimeHours = this.formatHours(
            response.minimum_overtime_hours
          );
          this.overTimeMinutes = this.formatMinutes(
            response.minimum_overtime_hours
          );
        }

        this.category.weekoffs = response.weekoffs.slice();
        // this.category = response;
      }
    },


    async submitCategoryDetails() {
      // if (!this.validateFields()) return;

      var full_day_hours = this.fullDayHours.split(" ")[0];
      if (full_day_hours.length == 1) full_day_hours = "0" + full_day_hours;

      var full_day_minutes = this.fullDayMinutes.split(" ")[0];
      if (full_day_minutes.length == 1)
        full_day_minutes = "0" + full_day_minutes;

      full_day_hours = full_day_hours + ":" + full_day_minutes + ":" + "00";

      var half_day_hours = this.halfDayHours.split(" ")[0];
      if (half_day_hours.length == 1) half_day_hours = "0" + half_day_hours;

      var half_day_minutes = this.halfDayMinutes.split(" ")[0];
      if (half_day_minutes.length == 1)
        half_day_minutes = "0" + half_day_minutes;

      half_day_hours = half_day_hours + ":" + half_day_minutes + ":" + "00";

      var buffer_hours = this.bufferHours.split(" ")[0];
      if (buffer_hours.length == 1) buffer_hours = "0" + buffer_hours;

      var buffer_minutes = this.bufferMinutes.split(" ")[0];
      if (buffer_minutes.length == 1) buffer_minutes = "0" + buffer_minutes;

      buffer_hours = buffer_hours + ":" + buffer_minutes + ":" + "00";

      var allowed_late_time = "00:00:00"
      if(!this.flexibleHoursSwitch){
        var allowedLateHours = this.allowed_late_hours.split(" ")[0];
        if (allowedLateHours.length == 1)
          allowedLateHours = "0" + allowedLateHours;

        var allowedLateMinutes = this.allowed_late_minutes.split(" ")[0];
        if (allowedLateMinutes.length == 1)
          allowedLateMinutes = "0" + allowedLateMinutes;

        allowed_late_time =
          allowedLateHours + ":" + allowedLateMinutes + ":" + "00";
      }


      var over_time_hours = "00:00:00";
      if (this.overTimeSwitch) {
        over_time_hours = this.overTimeHours.split(" ")[0];
        if (over_time_hours.length == 1)
          over_time_hours = "0" + over_time_hours;

        var over_time_minutes = this.overTimeMinutes.split(" ")[0];
        if (over_time_minutes.length == 1)
          over_time_minutes = "0" + over_time_minutes;

        over_time_hours =
          over_time_hours + ":" + over_time_minutes + ":" + "00";
      }

      if (!this.category.weekoffs) this.category.weekoffs = [];

      var post_body = {
        category: {
          title: this.category.title,
          weekoffs: this.category.weekoffs,
          full_day_hours: full_day_hours,
          half_day_hours: half_day_hours,
          allow_overtime: this.overTimeSwitch,
          minimum_overtime_hours: over_time_hours, //maybe dont send if overtimeswitch is false
          buffer_time: buffer_hours,
          allowed_late_time: allowed_late_time,
          flexible_hours: this.flexibleHoursSwitch,
          shift_start_time: this.shiftStartTime,
          shift_end_time: this.shiftEndTime,
          code: this.category.code,
        },
      };

      if (this.is_editing) {
        post_body.category.id = this.$route.params.id;
        post_body.is_editing = true;
      }

      var response;
      response = await api.handleApi({
        apiEssentials: this.essentials,
        url: endpoints.addEditStaffCategory,
        responseVar: response,
        method: api.Methods.POST,
        body: post_body,
      });
      if (response) {
        if (this.is_editing) {
          this.dialog = true;
        } 
        else{
          // let self = this;
          this.$router.push({ name: "AttendanceSettingsCategory" });

          // setTimeout(function () {
          //   self.$router.push({ name: "AttendanceSettingsCategory" });
          // }, 2000);
        }
      }
    },

    async showDeleteDialog() {
      if(this.category.used) {
        this.snackbar.message = "Category cannot be deleted since it has already been allocated to one more staff accounts.";
        this.snackbar.color = "red";
        this.snackbar.visible = true;
        return;
      }
      this.showDeleteConfirmation = true;
    },

    async changePastRecords() {
      this.dialog = false;
      var response = await api.call(
        this.essentials,
        endpoints.addEditStaffCategory,
        api.Methods.PATCH,
        {
          "category_id": this.$route.params.id,
        }
      );

      if(response){
        this.snackbar.message =
        "All the past records for the category will be changed";
        this.snackbar.visible = true;
        let self = this;
        setTimeout(function () {
          self.$router.push({ name: "AttendanceSettingsCategory" });
        }, 2000);
      }
    },

    closeDialog() {
      this.dialog = false;
      this.$router.push({ name: "AttendanceSettingsCategory" });
    },

    async deleteObjectConfirmation(confirm) {
      this.showDeleteConfirmation = false;
      if (confirm) {
        var response;
        response = await api.handleApi({
          apiEssentials: this.essentials,
          url: endpoints.addEditStaffCategory + this.category.id + "/",
          method: api.Methods.DELETE,
          responseVar: response,
        });
        if (response == true) {
          this.snackbar.message = "Category has been successfully deleted";
          this.snackbar.color = "red";
          this.snackbar.visible = true;
          this.$router.push({ name: "AttendanceSettingsCategory" });
        }
      }
    },

    async handleApiErrors(err) {
      this.setLoading(false);
      console.log(err);
      if (err.response) {
        if (err.response.status == 406) {
          this.formErrors.codeError = "Code is already used";
          this.snackbar.message =
            "Category Code is already used. Please use a different code";
          this.snackbar.color = "error";
          this.snackbar.visible = true;
        }
      } else {
        this.snackbar.message = "Unable to process request";
        this.snackbar.color = "error";
        this.snackbar.visible = true;
      }
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.is_editing = true;
      await this.getCategoryDetails();
    }

    // this.submitForm = false;
  },
};
</script>


<style scoped>
.text-white {
  color: white !important;
}
.rounded-input {
  border-radius: 12px !important;
}
</style>
